import { useCallback } from 'react';
import { t } from '@lingui/macro';
import { IPurchaseContext } from 'controllers/business/Purchase/Purchase.types';
import { usePrivateActionClickHandler } from 'hooks/usePrivateActionClickHandler/usePrivateActionClickHandler';
import { getDownloadableItem } from 'helpers/getDownloadableItem';
import { AuthenticatedActions } from 'types/authenticatedUserAction';
import { DownloadableItemSource } from 'types';
import {
    RequestCostPreviewQueries,
    RequestCostPreviewQueryItemName,
} from 'apollo/cost';
import { useSubscriptionsAndAllowedFeaturesContext } from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import { useFreeUserFreeDownloadUpsellModalContext } from 'modals/UpsellModals/FreeUserFreeDownloadUpsellModalContext';
import { useApolloClient } from '@apollo/client';

export type DownloadClickHandlerParams = {
    item: DownloadableItemSource;
    purchaseController: IPurchaseContext;
};

export const useDownloadClickHandler = ({
    item,
    purchaseController,
}: DownloadClickHandlerParams) => {
    const {
        isLoading: purchaseLoading,
        queuedItemIDs,
        startPurchase,
    } = purchaseController;

    const downloadableItem = getDownloadableItem(item);

    const isLoading =
        purchaseLoading && queuedItemIDs.includes(downloadableItem.id);

    const { upsellingAllowed } = useSubscriptionsAndAllowedFeaturesContext();

    const { openModal } = useFreeUserFreeDownloadUpsellModalContext();

    const apolloClient = useApolloClient();

    const handleClick = useCallback(() => {
        async function execute() {
            const { data } = await apolloClient.query({
                query: RequestCostPreviewQueries[downloadableItem.type],
                fetchPolicy: 'no-cache',
                variables: {
                    guid: downloadableItem.itemIds,
                },
            });

            const costPreviewData =
                data[RequestCostPreviewQueryItemName[downloadableItem.type]];

            const isFree = costPreviewData.credits === 0;

            if (upsellingAllowed && isFree) {
                openModal(downloadableItem);
            } else {
                startPurchase({ item: downloadableItem });
            }
        }

        execute();
    }, [
        startPurchase,
        downloadableItem,
        upsellingAllowed,
        openModal,
        apolloClient,
    ]);

    const handleDownloadFullTrackClick = usePrivateActionClickHandler({
        authOnly: true,
        onClick: handleClick,
        preventDefault: true,
        stopPropagation: true,
        isLoading,
        userAction: {
            type: AuthenticatedActions.downloadItem,
            description: t`download`,
            args: [downloadableItem],
        },
    });

    return handleDownloadFullTrackClick;
};
